import ProjectMixin from '@/components/Project/Mixins/projectMixin.js';

const API_ERROR_MESSAGE = 'An error occured processing your request. Please try again later.';

export default {
  props: {
    propReadOnly: {
      default: () => false,
      type: Boolean,
    },
  },
  mixins: [ProjectMixin],
  data() {
    return {
      emptyTextAnalysisObject: {
        uuid: '',
        datasetId: '',
        selectedTextQuestion: {},
        selectedResponseIds: [],
        filterParams: {},
        last_modified_on: '',
        // Banning and hiding
        bannedComments: [],
        bannedKeywords: [],
        hiddenComments: [],
        // Analysis options
        activeTab: 'Themes',
        includeSentiments: 'exclude',
        showCoverageDetails: 'show',
        sentimentSortOrder: -1,
        themesSortOrder: 'descending',
        themesSortType: 'coverage',
        expandedResponses: [],
        includeSmartCoverage: 'exclude',
        // Saving
        saved_node_ids: [],
        thisContentChanged: false,
        // Themes
        themes: [], // This has been removed from this object and moved to its own db collection as of 1.7.0
        // selectedThemeUUID: "", // Deprecated in 1.9.0 in favour of the below array
        selectedThemeUUIDs: [],
        // Sentiments
        adjustedSentiments: [],
        // When running a search
        search: {
          searchString: '',
          alsoFound: [],
          searchResultsIds: [],
          searchResults: [],
          excludeAssociations: false,
          wordAssociations: [],
          similarSentences: [],
          searchMode: 'exact matches',
          isFetchingWordAssociations: false,
        },
        // Filter Parameters (ResearchAI-special logic)
        time_series: 'None',
        time_filter_name: 'All data',
        // Spinners (why do we have two?)
        showResponseListLoadingSpinner: false,
        showSpinner: false,
      },
    };
  },
  methods: {
    // ***
    // INITIAL LOGIC SECTION
    async fetchEmptyTextAnalysis() {
      try {
        return this.emptyTextAnalysisObject;
      } catch (e) {
        throw new Error('textAnalysisMixin:summaryResults ' + e.message);
      }
    },
    // This function will fetch all text responses from a question
    // It will include a "sentiment" object. This is an initial 'suggested' sentiment
    async fetchTextQuestionResponses(question, filter_params) {
      if (!filter_params) {
        filter_params = {};
      }
      if (!question._id) return [];

      let proj_id = this.project.id;
      if (this.project.id.$oid) {
        proj_id = this.project.id.$oid;
      }
      const questionResponses = await this.DATASETS_SERVICE.getClientQuestionTextResponses({
        client_question_id: question._id,
        filter_params: filter_params,
        project_id: proj_id,
        text_analysis_uuid: this.textAnalysisObject.uuid,
      });

      // ** Additional cleaning **
      // Remove NaN
      const clonedResponse = this.deepCloneObj(questionResponses);

      let NoNaNResponse = [];
      if (clonedResponse.length) {
        for (let i = 0; i < clonedResponse.length; i++) {
          let foundNaN = false;
          for (let x = 0; x < clonedResponse[i].response.length; x++) {
            if (
              clonedResponse[i].response[x].word == 'nan' ||
              clonedResponse[i].response == 'nan'
            ) {
              foundNaN = true;
            }
          }
          if (foundNaN == false) {
            NoNaNResponse.push(clonedResponse[i]);
          }
        }
      }
      return NoNaNResponse;
    },

    // ***
    // SAVE USER INTERACTION LOGIC SECTION
    async processAndSave(saveParams) {
      if (saveParams) {
        if (saveParams.uuid) {
          // let preparedThemes = []
          for (let i = 0; i < this.project.textAnalysisObjects.length; i++) {
            if (this.project.textAnalysisObjects[i].uuid === saveParams.uuid) {
              if (saveParams.selectedTextQuestion) {
                if (
                  saveParams.selectedTextQuestion._id &&
                  saveParams.selectedTextQuestion._id.$oid
                ) {
                  saveParams.selectedTextQuestion._id = saveParams.selectedTextQuestion._id.$oid;
                }
                this.project.textAnalysisObjects[i].selectedTextQuestion =
                  saveParams.selectedTextQuestion;
              }
              // moved from textAnalysisObject to separate DB object in 1.7.0, but can be removed during transition
              if (saveParams.themes) {
                this.textAnalysisObject.themes = [];
                this.project.textAnalysisObjects[i].themes = [];
              }

              // In 1.9.0 moved from a single theme to an array of themes
              if (saveParams.isChangingThemeUUID) {
                this.project.textAnalysisObjects[i].selectedThemeUUIDs =
                  saveParams.selectedThemeUUIDs;
                this.textAnalysisObject.selectedThemeUUIDs = saveParams.selectedThemeUUIDs;
              }
              if (saveParams.isChangingThemeUUID) {
                this.project.textAnalysisObjects[i].isChangingThemeUUID =
                  saveParams.isChangingThemeUUID;
                this.textAnalysisObject.isChangingThemeUUID = saveParams.isChangingThemeUUID;
              }

              if (saveParams.includeSentiments) {
                this.project.textAnalysisObjects[i].includeSentiments =
                  saveParams.includeSentiments;
                this.textAnalysisObject.includeSentiments = saveParams.includeSentiments;
              }
              if (saveParams.showCoverageDetails) {
                this.project.textAnalysisObjects[i].showCoverageDetails =
                  saveParams.showCoverageDetails;
                this.textAnalysisObject.showCoverageDetails = saveParams.showCoverageDetails;
              }
              if (saveParams.includeSmartCoverage) {
                this.project.textAnalysisObjects[i].includeSmartCoverage =
                  saveParams.includeSmartCoverage;
                this.textAnalysisObject.includeSmartCoverage = saveParams.includeSmartCoverage;
              }
              if (saveParams.adjustedSentiments) {
                this.project.textAnalysisObjects[i].adjustedSentiments =
                  saveParams.adjustedSentiments;
              }
              if (saveParams.allCharts) {
                this.project.textAnalysisObjects[i].allCharts = saveParams.allCharts;
              }

              if (saveParams.expandedResponses) {
                this.project.textAnalysisObjects[i].expandedResponses =
                  saveParams.expandedResponses;
              }
              if (saveParams.bannedComments) {
                this.project.textAnalysisObjects[i].bannedComments = saveParams.bannedComments;
              }
              if (saveParams.hiddenComments) {
                this.project.textAnalysisObjects[i].hiddenComments = saveParams.hiddenComments;
              }
              if (saveParams.showSpinner) {
                // are we using this?
                this.project.textAnalysisObjects[i].showSpinner = saveParams.showSpinner;
              }
              if (saveParams.activeTab) {
                this.project.textAnalysisObjects[i].activeTab = saveParams.activeTab;
              }
              if (saveParams.sentimentSortOrder <= 0 || saveParams.sentimentSortOrder > 0) {
                this.project.textAnalysisObjects[i].sentimentSortOrder =
                  saveParams.sentimentSortOrder;
                this.textAnalysisObject.sentimentSortOrder = saveParams.sentimentSortOrder;
              }
              if (saveParams.selectedResponseIds) {
                this.project.textAnalysisObjects[i].selectedResponseIds =
                  saveParams.selectedResponseIds;
                this.textAnalysisObject.selectedResponseIds = saveParams.selectedResponseIds;
              }
              if (saveParams.bannedKeywords) {
                this.project.textAnalysisObjects[i].bannedKeywords = saveParams.bannedKeywords;
                this.textAnalysisObject.bannedKeywords = saveParams.bannedKeywords;
              }
              if (saveParams.search) {
                this.project.textAnalysisObjects[i].search = saveParams.search;
                this.textAnalysisObject.search = saveParams.search;
              }
            }
          }
          if (saveParams.runSortAndSelect) {
            this.emitSortAndSelect();
          }
          let skipUpdateEmit = false;
          if (saveParams.skipUpdateEmit) {
            skipUpdateEmit = true;
          }
          return await this.cleanAndSaveProject(skipUpdateEmit);
        }
      }
    },
    // Note that projectReportMixin and Project.vue have a function of the same name. To do: Rename
    async cleanAndSaveProject(skipUpdateEmit) {
      this.thisNum = this.thisNum + 1;
      let cleanedProject = this.deepCloneObj(this.project);
      if (cleanedProject) {
        cleanedProject.id = this.project.id;
        if (cleanedProject._id) {
          delete cleanedProject._id;
        }
        if (!this.propReadOnly) {
          this.saveProject(cleanedProject);
        }

        // The child that calls this chain of saving functions should have this function, to tell the parent in ProjectAnalysisTextItem to update
        if (this.updateTextObject) {
          if (!skipUpdateEmit) {
            this.updateTextObject(cleanedProject);
          }
        }
        return 'Success';
      }
    },
    async saveSelectedTextQuestion(question) {
      await this.processAndSave(question);
    },
    // Keeping to allow pre 1.7.0 themes to be removed
    async saveTextThemes(saveParams) {
      this.showModalMessage(null, null);
      await this.processAndSave(saveParams);
    },
    async saveDeletedTheme(theme_id) {
      if (theme_id.$oid) {
        theme_id = theme_id.$oid;
      }

      let payload = {
        _id: theme_id,
        text_analysis_uuid: this.textAnalysisObject.uuid,
      };
      await this.TEXT_SERVICE.deleteTextTheme(payload);
    },

    // used to allow text question to be changed. Can delete chain.
    async selectTextQuestion(question) {
      this.textAnalysisObject.selectedTextQuestion = question;
      return this.textAnalysisObject.selectedTextQuestion;
    },

    async setShowCoverageDetails(coverageOption) {
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        showCoverageDetails: coverageOption,
      };
      this.processAndSave(saveParams);
    },
    async setIncludeSmartCoverage(smartSearchOption) {
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        includeSmartCoverage: smartSearchOption,
      };
      this.processAndSave(saveParams);
    },
    async updateAllCharts(allCharts) {
      this.textAnalysisObject.allCharts = allCharts;
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        allCharts: allCharts,
      };
      this.processAndSave(saveParams);
    },
    async adjustSentimentItem(sentimentUpdates) {
      let newSentiment = parseFloat(sentimentUpdates.newSentiment);
      if (sentimentUpdates.responseId && newSentiment >= -1 && newSentiment <= 1) {
        let foundExistingMatch = false;
        if (this.textAnalysisObject.adjustedSentiments.length) {
          for (let i = 0; i < this.textAnalysisObject.adjustedSentiments.length; i++) {
            if (
              this.textAnalysisObject.adjustedSentiments[i].responseId ==
              sentimentUpdates.responseId
            ) {
              this.textAnalysisObject.adjustedSentiments[i].adjustedSentiment = newSentiment;
              await this.saveAdjustedSentiments({
                adjustedSentiments: this.textAnalysisObject.adjustedSentiments,
                forceCompute: true,
                uuid: this.textAnalysisObject.uuid,
              });
              foundExistingMatch = true;
              break;
            }
          }
        }
        if (foundExistingMatch == false) {
          let newSentimentObject = {
            responseId: sentimentUpdates.responseId,
            adjustedSentiment: newSentiment,
          };
          this.textAnalysisObject.adjustedSentiments.push(newSentimentObject);
          await this.saveAdjustedSentiments({
            adjustedSentiments: this.textAnalysisObject.adjustedSentiments,
            forceCompute: true,
            uuid: this.textAnalysisObject.uuid,
          });
        }
      }
    },
    async banResponseItem(responseId) {
      const bannedComments = [...this.textAnalysisObject.bannedComments];
      // const bannedComments__ORIGINAL = _.clone(bannedComments)

      let newId = responseId;
      if (responseId.$oid) {
        newId = responseId;
      }
      if (bannedComments.includes(newId)) return;
      this.textAnalysisObject.bannedComments.push(newId);
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        bannedComments: this.textAnalysisObject.bannedComments,
      };
      const preparedAndSaved = await this.processAndSave(saveParams);
      if (preparedAndSaved) {
        this._alert('Response successfully banned.', 'Success', 'success', 'banResponseItem');
      } else {
        this._alert(API_ERROR_MESSAGE, 'Error', 'error', 'banResponseItem');
        throw new Error('textAnalysisMixin.js:banResponseItem: ');
      }
    },
    async banResponseItems(responseIds) {
      let oldBanned = this.deepCloneObj(this.textAnalysisObject.bannedComments);
      if (!oldBanned) {
        oldBanned = [];
      }

      let filteredBanned = responseIds.filter((filter) => !oldBanned.includes(filter));
      this.textAnalysisObject.bannedComments =
        this.textAnalysisObject.bannedComments.concat(filteredBanned);

      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        bannedComments: this.textAnalysisObject.bannedComments,
      };
      const preparedAndSaved = await this.processAndSave(saveParams);
      if (preparedAndSaved) {
        this._alert('Response successfully banned.', 'Success', 'success', 'banResponseItem');
      } else {
        this._alert(API_ERROR_MESSAGE, 'Error', 'error', 'banResponseItem');
        throw new Error('textAnalysisMixin.js:banResponseItem: ');
      }
    },
    async setResponseAsExpanded(responseId, skipUpdateEmit) {
      if (!responseId) return;

      if (this.textAnalysisObject.expandedResponses) {
        let found = false;
        for (let i = 0; i < this.textAnalysisObject.expandedResponses.length; i++) {
          if (this.textAnalysisObject.expandedResponses[i] == responseId) {
            found = true;
          }
        }
        if (!found) {
          this.textAnalysisObject.expandedResponses.push(responseId);
        } else {
          this.textAnalysisObject.expandedResponses =
            this.textAnalysisObject.expandedResponses.filter((response) => response !== responseId);
        }
      } else {
        this.textAnalysisObject.expandedResponses = [];
        this.textAnalysisObject.expandedResponses.push(responseId);
      }

      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        expandedResponses: this.textAnalysisObject.expandedResponses,
        skipUpdateEmit: skipUpdateEmit,
      };
      await this.processAndSave(saveParams);
    },
    async toggleResponseItemHide(responseId) {
      if (responseId) {
        let newId = responseId;
        if (responseId.$oid) {
          newId = responseId.$oid;
        }
        const hiddenComments = [...this.textAnalysisObject.hiddenComments];
        if (hiddenComments.includes(newId)) {
          hiddenComments.splice(hiddenComments.indexOf(newId), 1);
        } else {
          hiddenComments.push(newId);
        }
        this.textAnalysisObject.hiddenComments = hiddenComments;
        const saveParams = {
          forceCompute: false,
          uuid: this.textAnalysisObject.uuid,
          hiddenComments: this.textAnalysisObject.hiddenComments,
        };
        await this.processAndSave(saveParams);
      }
    },
    // This takes an already formatted hidden comment array. It does no other cleaning.
    // When using be sure to remove $oid ahead of time to avoid needing to clean it out here.
    async toggleResponseItemsHide(responseIds) {
      if (responseIds.length) {
        this.textAnalysisObject.hiddenComments = responseIds;
        const saveParams = {
          forceCompute: false,
          uuid: this.textAnalysisObject.uuid,
          hiddenComments: this.textAnalysisObject.hiddenComments,
        };
        await this.processAndSave(saveParams);
      }
    },
    async saveBanKeyword(keyword) {
      if (this.textAnalysisObject.bannedKeywords.includes(keyword.toLowerCase())) {
        this.textAnalysisObject.bannedKeywords = this.textAnalysisObject.bannedKeywords.filter(
          (obj) => obj !== keyword,
        );
      } else {
        this.textAnalysisObject.bannedKeywords.push(keyword);
      }
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        bannedKeywords: this.textAnalysisObject.bannedKeywords,
      };
      this.processAndSave(saveParams);
    },
    async setSearch(obj) {
      this.textAnalysisObject.search = {
        searchString: '',
        alsoFound: [],
        searchResults: [],
        searchResultsIds: [],
        excludeAssociations: false,
        wordAssociations: [],
        similarSentences: [],
        searchMode: 'exact matches',
        isFetchingWordAssociations: false,
      };
      if (obj.searchString) {
        this.textAnalysisObject.search.searchString = obj.searchString;
      }
      if (obj.searchResultsIds) {
        this.textAnalysisObject.search.searchResultsIds = obj.searchResultsIds; // not currently used by either regular search or similar sentences
      }
      if (obj.searchResults) {
        this.textAnalysisObject.search.searchResults = obj.searchResults;
      }
      if (obj.similarSentences) {
        this.textAnalysisObject.search.similarSentences = obj.similarSentences;
      }
      if (obj.searchMode) {
        this.textAnalysisObject.search.searchMode = obj.searchMode;
      }
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        search: this.textAnalysisObject.search,
        skipUpdateEmit: true,
        runSortAndSelect: true,
      };
      await this.processAndSave(saveParams);
    },
    async resetSearch() {
      this.textAnalysisObject.search = {
        searchString: '',
        alsoFound: [],
        searchResults: [],
        searchResultsIds: [],
        excludeAssociations: false,
        wordAssociations: [],
        similarSentences: [],
        searchMode: 'exact matches',
        isFetchingWordAssociations: false,
      };
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        search: this.textAnalysisObject.search,
        skipUpdateEmit: true,
      };
      await this.processAndSave(saveParams);
    },
    async setShowResponseListLoadingSpinner(bool) {
      this.textAnalysisObject.showResponseListLoadingSpinner = bool;
    },
    async setShowSpinner(bool) {
      this.textAnalysisObject.setShowSpinner = bool;
    },
    async setActiveTab(tab) {
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        activeTab: tab,
      };
      await this.processAndSave(saveParams);
      this.textAnalysisObject.activeTab = saveParams.activeTab;
    },

    async setSelectedThemeUUIDs(uuids) {
      let useTheseUUIDs = uuids;
      if (!uuids) {
        useTheseUUIDs = [];
      }
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        selectedThemeUUIDs: useTheseUUIDs,
        isChangingThemeUUID: true,
        skipUpdateEmit: true,
      };
      this.processAndSave(saveParams);
    },
    async setSentimentSortOrder(responses) {
      this.textAnalysisObject.sentimentSortOrder = responses;
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        sentimentSortOrder: responses,
      };
      this.processAndSave(saveParams);
    },
    async setSelectedResponseIds(respondIds) {
      let useThese = [];
      if (!respondIds || !respondIds.length) {
        useThese = [];
      } else {
        useThese = respondIds;
      }
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        selectedResponseIds: useThese,
      };
      this.processAndSave(saveParams);
    },
    async saveAdjustedSentiments(adjustedSentiments) {
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        adjustedSentiments: adjustedSentiments.adjustedSentiments,
      };
      this.processAndSave(saveParams);
    },
    selectionChanged(selection) {
      let filterByResponseId = [];
      for (let x = 0; x < selection.length; x++) {
        let selectedId = selection[x].id;
        if (selection[x].id.$oid) {
          selectedId = selection[x].id.$oid;
        }
        filterByResponseId.push(selectedId);
      }
      this.setSelectedResponseIds(filterByResponseId);
    },
    setBannedKeywords(array) {
      const saveParams = {
        forceCompute: false,
        uuid: this.textAnalysisObject.uuid,
        bannedKeywords: array,
      };
      this.processAndSave(saveParams);
    },
    setTextAnalysisObject(object) {
      this.textAnalysisObject = object;
    },
    async generateThemesFromModels(prop) {
      try {
        const response = await this.TEXT_SERVICE.matchResponsesToCustomTopics({
          client_question_id: prop.questionId,
          project_id: this.project.id,
          text_analysis_uuid: this.textAnalysisObject.uuid,
          filter_params: prop.filter_params,
          smart_coverage: prop.smart_coverage,
          model_ids: prop.model_ids,
          include_all_themes: prop.include_all_themes,
          include_machine_learning: prop.include_machine_learning,
        });

        return response;
      } catch (e) {
        console.error('ProjectAnalysisTextTheme.vue:generateThemes: ' + e.message); // eslint-disable-line
      }
    },

    // THEME LOGIC SECTION
    async generateBertSentenceTopics(prop) {
      try {
        const response = await this.TEXT_SERVICE.bertSentenceTopicsGenerator({
          client_question_id: prop.questionId,
          project_id: this.project.id,
          smart_coverage: prop.smart_coverage,
          filter_params: prop.filter_params,
          target_themes: prop.target_themes,
          granularity: prop.granularity,
          text_analysis_uuid: prop.text_analysis_uuid,
        });
        if (response && response.error) {
          return 'error: too small';
        } else {
          return response;
        }
      } catch (e) {
        console.error('ProjectAnalysisTextTheme.vue:generateThemes: ' + e.message); // eslint-disable-line
      }
    },

    // THEME LOGIC SECTION
    async generateLDATopics(prop) {
      try {
        const response = await this.TEXT_SERVICE.generateLDATopicGenerator({
          client_question_id: prop.questionId,
          project_id: this.project.id,
          smart_coverage: prop.smart_coverage,
          filter_params: prop.filter_params,
          text_analysis_uuid: prop.text_analysis_uuid,
        });
        if (response && response.error) {
          return 'error: too small';
        } else {
          return response;
        }
      } catch (e) {
        console.error('ProjectAnalysisTextTheme.vue:generateLDATopics: ' + e.message); // eslint-disable-line
      }
    },

    async updateExistingThemeTitle(theme, title) {
      if (typeof title !== 'string' || title.trim() === '') {
        title = 'unnamed theme';
      }

      const newTitle = title.trim();
      const themeId = theme._id;

      // Duplicate name check
      const nameExists = this.sortedThemes.some(
        (t) =>
          t._id !== themeId && t.name && t.name.trim().toLowerCase() === newTitle.toLowerCase(),
      );

      if (nameExists) {
        this.duplicateIndex = this.sortedThemes.findIndex((t) => t._id === themeId);
        setTimeout(() => (this.duplicateIndex = -1), 6000);
        return;
      }

      // Optimistic UI
      const localTheme = this.sortedThemes.find((t) => t._id === themeId);
      if (localTheme) {
        localTheme.name = newTitle;
      }
      // Atomic patch (consistent with theme_color)
      this.$emit('updateTheme', {
        _id: themeId,
        fields_to_update: {
          name: newTitle,
        },
      });
    },

    parseCoveragePercentage(theme, sortedResponsesLength) {
      let numerator = 0;
      // Note: for the below we could use matching_keywords.length, or total, both should be equal
      if (
        this.textAnalysisObject.filterParams &&
        Object.keys(this.textAnalysisObject.filterParams).length &&
        theme.filtered_coverage?.matching_keywords
      ) {
        if (theme.filtered_coverage.total > -1) {
          numerator = theme.filtered_coverage.total;
        }
        let denominator = null;
        if (theme) {
          denominator = sortedResponsesLength;
        }
        return ((parseInt(numerator) / parseInt(denominator)) * 100).toFixed(1);
      } else {
        if (theme.coverage.total > -1) {
          numerator = theme.coverage.total;
        }
        let denominator = null;
        if (theme) {
          denominator = sortedResponsesLength;
        }
        return ((parseInt(numerator) / parseInt(denominator)) * 100).toFixed(1);
      }
    },

    // ***
    // MAIN FILTERING LOGIC SECTION

    filterBannedKeywords(keywords) {
      if (!keywords || !keywords.length) {
        return [];
      }
      if (
        !this.textAnalysisObject.bannedKeywords ||
        !this.textAnalysisObject.bannedKeywords.length
      ) {
        return keywords;
      }
      return keywords.filter(
        (el) => this.textAnalysisObject.bannedKeywords.indexOf(el.trim().toLowerCase()) === -1,
      );
    },
    sortTextResponses(responses) {
      if (!responses || !responses.length) {
        this.sortedResponses = [];
        return [];
      }
      if (this.hasPreProcessedText && this.textAnalysisObject.includeSentiments == 'include') {
        let clonedResponses = this.deepCloneObj(responses);
        // move below to a filter - optimization TODO
        if (this.textAnalysisObject.adjustedSentiments) {
          for (let i = 0; i < this.textAnalysisObject.adjustedSentiments.length; i++) {
            for (let x = 0; x < clonedResponses.length; x++) {
              if (
                this.textAnalysisObject.adjustedSentiments[i].responseId == clonedResponses[x].id
              ) {
                clonedResponses[x].response_sentiment_bert.compound_score =
                  this.textAnalysisObject.adjustedSentiments[i].adjustedSentiment;
                break;
              }
            }
          }
        }
        if (this.textAnalysisObject.sentimentSortOrder < 0) {
          clonedResponses.sort(
            (a, b) =>
              a.response_sentiment_bert.compound_score - b.response_sentiment_bert.compound_score,
          );
        }
        if (this.textAnalysisObject.sentimentSortOrder > 0) {
          clonedResponses.sort(
            (a, b) =>
              b.response_sentiment_bert.compound_score - a.response_sentiment_bert.compound_score,
          );
        }
        if (this.textAnalysisObject.sentimentSortOrder == 0) {
          clonedResponses.sort((a, b) => a.row_index - b.row_index);
        }

        this.sortedResponses = clonedResponses;
      } else {
        this.sortedResponses = responses.sort((a, b) => a.row_index - b.row_index);
      }
    },

    async sortThemes() {
      if (!this.unsortedThemes || !this.unsortedThemes.length) {
        this.sortedThemes = [];
      }
      let themes = this.deepCloneObj(this.unsortedThemes);

      if (this.textAnalysisObject.themesSortType == 'created') {
        if (this.textAnalysisObject.themesSortOrder == 'ascending') {
          themes.sort((a, b) => a.created_on - b.created_on);
        } else {
          themes.sort((a, b) => b.created_on - a.created_on);
        }
      } else if (this.textAnalysisObject.themesSortType == 'alphabetical') {
        if (this.textAnalysisObject.themesSortOrder == 'ascending') {
          themes.sort((a, b) => a.name.localeCompare(b.name));
        } else {
          themes.sort((a, b) => b.name.localeCompare(a.name));
        }
      } else if (this.textAnalysisObject.themesSortType == 'coverage') {
        if (this.textAnalysisObject.themesSortOrder == 'ascending') {
          if (
            this.textAnalysisObject.filterParams &&
            Object.keys(this.textAnalysisObject.filterParams).length
          ) {
            themes.sort((a, b) => a.filtered_coverage.total - b.filtered_coverage.total);
          } else {
            themes.sort((a, b) => a.coverage.total - b.coverage.total);
          }
        } else {
          if (
            this.textAnalysisObject.filterParams &&
            Object.keys(this.textAnalysisObject.filterParams).length
          ) {
            themes.sort((a, b) => b.filtered_coverage.total - a.filtered_coverage.total);
          } else {
            themes.sort((a, b) => b.coverage.total - a.coverage.total);
          }
        }
      } else if (this.textAnalysisObject.themesSortType == 'sentiment') {
        if (this.textAnalysisObject.themesSortOrder == 'ascending') {
          themes.sort((a, b) => {
            const aHas = a.sentiment && typeof a.sentiment.compound === 'number';
            const bHas = b.sentiment && typeof b.sentiment.compound === 'number';

            // If both have compound → sort by it
            if (aHas && bHas) {
              return a.sentiment.compound - b.sentiment.compound;
            }

            // If one has compound and the other doesn't → keep original order
            if (aHas !== bHas) {
              return 0;
            }

            // Neither has compound → keep original order
            return 0;
          });
        } else {
          themes.sort((a, b) => {
            const aHas = a.sentiment && typeof a.sentiment.compound === 'number';
            const bHas = b.sentiment && typeof b.sentiment.compound === 'number';

            // If both have compound → sort by it
            if (aHas && bHas) {
              return b.sentiment.compound - a.sentiment.compound;
            }

            // If one has compound and the other doesn't → keep original order
            if (aHas !== bHas) {
              return 0;
            }

            // Neither has compound → keep original order
            return 0;
          });
        }
      }
      this.sortedThemes = themes;
    },

    // ***
    // UTILITIES SECTION

    getSentimentLabel(score) {
      if (score < -0.75) {
        return 'Very negative 😡';
      } else if (score < -0.25) {
        return 'Negative 😟';
      } else if (score < 0.25) {
        return 'Neutral 😐';
      } else if (score < 0.75) {
        return 'Positive 🙂';
      } else {
        return 'Very positive 😃';
      }
    },
    getSentimentEmoji(score) {
      if (score < -0.75) {
        return '😡';
      } else if (score < -0.25) {
        return '😟';
      } else if (score < 0.25) {
        return '😐';
      } else if (score < 0.75) {
        return '🙂';
      } else {
        return '😃';
      }
    },
    getSentimentStyle(score) {
      if (score < -0.75) {
        return 'background-color: var(--very-negative-soft); color: white;';
      } else if (score < -0.25) {
        return 'background-color: var(--somewhat-negative-soft);';
      } else if (score < 0.25) {
        return '';
      } else if (score < 0.75) {
        return 'background-color: var(--somewhat-positive-soft)';
      } else {
        return 'background-color: var(--very-positive-soft)';
      }
    },

    generateSentimentText(score) {
      return score >= 0
        ? `${(score * 100).toFixed(1)}% positive sentiment`
        : `${(score * -100).toFixed(1)}% negative sentiment`;
    },
    getModalRef() {
      return this.$refs[this.modalRef];
    },
    showModalMessage(messageType, message) {
      this.getModalRef() && this.getModalRef().showMessage(messageType, message);
    },
    getApiLanguage() {
      return window.localStorage.getItem('apiLanguage') || '';
    },
    deepCloneObj(obj) {
      // deep clones an object using JSON stringify (data loss might occur)
      if (Array.isArray(obj)) {
        return obj.map((item) => JSON.parse(JSON.stringify(item)));
      } else if (typeof obj == 'object') {
        return JSON.parse(JSON.stringify(obj));
      }
    },
    arrayEquals(array1, array2) {
      return JSON.stringify(array1.sort()) === JSON.stringify(array2.sort());
    },
    isEmpty(theme) {
      return !theme || (theme.keyword_objects.length === 0 && theme.notes.length === 0);
    },
    _alert(message, title, confirmType, confirmSourceComponent, btnText = 'Okay') {
      this.setConfirmText({
        btn: btnText,
        title: title,
        message: message,
      });
      this.setConfirmType(confirmType);
      this.setConfirmSourceComponent(confirmSourceComponent);
      this.setConfirmIsVisible(true);
    },
    normalizeKeywords(theme) {
      if (Array.isArray(theme.keywords)) {
        // Legacy format
        return theme.keywords;
      }
      if (Array.isArray(theme.keyword_objects)) {
        return theme.keyword_objects.map((ko) => ko.keyword);
      }
      return [];
    },
  },
  computed: {
    TEXT_SERVICE() {
      return this.$services.TEXT_SERVICE;
    },
    modalRef: function () {
      return `modal-${this.$.uid}`;
    },
    modalOpen() {
      return this.$store.getters['getModalOpen'];
    },
    sortMode() {
      return this.textAnalysisObject.sentimentSortOrder;
    },
    currentSelectedResponseIds() {
      return this.textAnalysisObject.selectedResponseIds;
    },
    isSearchMode: function () {
      if (this.textAnalysisObject && this.textAnalysisObject.search) {
        return this.textAnalysisObject.search.searchString.trim() !== '';
      }
      return false;
    },
    hasSearchResults: function () {
      if (this.textAnalysisObject.search) {
        if (this.textAnalysisObject.search.searchResults.length) {
          return true;
        }
      }
      return false;
    },
    hasSimilarSentences: function () {
      if (this.textAnalysisObject.search) {
        if (this.textAnalysisObject.search.similarSentences.length) {
          return true;
        }
      }
      return false;
    },
    areThemeSelected: function () {
      if (!this.textAnalysisObject) {
        return false;
      }
      return !this.textAnalysisObject.selectedThemeUUIDs.length;
    },
    isVisFilterActivated: function () {
      if (!this.textAnalysisObject) {
        return false;
      }
      return this.textAnalysisObject.selectedResponseIds.length > 0;
    },
    searchResponseIds: function () {
      if (!this.textAnalysisObject) {
        return false;
      }
      return this.textAnalysisObject.search.searchResultsIds;
    },
    selectedTextQuestionId: function () {
      if (!this.textAnalysisObject.selectedTextQuestion) return undefined;
      let textQuestion;
      if (this.textAnalysisObject.selectedTextQuestion._id) {
        if (this.textAnalysisObject.selectedTextQuestion._id.$oid) {
          textQuestion = this.textAnalysisObject.selectedTextQuestion._id.$oid;
        } else {
          textQuestion = this.textAnalysisObject.selectedTextQuestion._id;
        }
      }
      return textQuestion;
    },
  },
};
